/**
 * SamlelisteFlow
 * 
 * Multi-step wizard for Samleliste workflow:
 * 1. Company Selection
 * 2. Order Number (auto-generated or manual)
 * 3. Location Selection (if company has locations)
 * 4. → SamlelistePage
 */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase/client'
import { 
  Building2, 
  FileText, 
  MapPin,
  ChevronRight,
  Loader2,
  Edit3,
  Check
} from 'lucide-react'
import { usePageHeader } from '@/components/layout/pageHeader'

// Touch-optimized dimensions
const TOUCH = {
  minTarget: 44,
  buttonHeight: 'min-h-[56px]',
  inputHeight: 'min-h-[56px]',
}

interface Company {
  id: string
  name: string
  report_code: string | null
}

// Commonly used companies - shown as quick-access buttons
const COMMON_COMPANIES = [
  'SLB',
  'TechnipFMC',
  'CCB',
  'Enhanced Drilling',
  'Beerenberg',
  'Air Liquide',
]

interface CompanyLocation {
  id: string
  name: string
  description: string | null
  is_default: boolean
}

type FlowStep = 'company' | 'order' | 'location'

export function SamlelisteFlow() {
  const navigate = useNavigate()
  
  // Flow state
  const [currentStep, setCurrentStep] = useState<FlowStep>('company')
  const [loading, setLoading] = useState(false)
  
  // Company state
  const [companies, setCompanies] = useState<Company[]>([])
  const [filteredCompanies, setFilteredCompanies] = useState<Company[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null)
  
  // Order number state
  const [orderNumber, setOrderNumber] = useState('')
  const [isManualOrder, setIsManualOrder] = useState(false)
  
  // Service Order state (TechnipFMC specific - uses so_no instead of order_no)
  const [serviceOrder, setServiceOrder] = useState('')
  const [isTechnipFMC, setIsTechnipFMC] = useState(false)
  
  // Location state
  const [companyLocations, setCompanyLocations] = useState<CompanyLocation[]>([])
  const [selectedLocation, setSelectedLocation] = useState('')
  
  // Load companies on mount
  useEffect(() => {
    loadCompanies()
  }, [])

  const loadCompanies = async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('id, name, report_code')
        .order('name', { ascending: true })

      if (error) throw error
      setCompanies(data || [])
      setFilteredCompanies(data || [])
    } catch (err) {
      console.error('Failed to load companies:', err)
    } finally {
      setLoading(false)
    }
  }

  // Filter companies based on search
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredCompanies(companies)
    } else {
      const filtered = companies.filter(company =>
        company.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
      setFilteredCompanies(filtered)
    }
  }, [searchQuery, companies])

  // Get commonly used companies from the loaded companies list
  const commonCompanies = companies.filter(company =>
    COMMON_COMPANIES.some(name => 
      company.name.toLowerCase().includes(name.toLowerCase()) ||
      name.toLowerCase().includes(company.name.toLowerCase())
    )
  )

  // Generate order number from report_code
  const generateOrderNumber = useCallback((reportCode: string | null) => {
    if (!reportCode) return ''
    
    const now = new Date()
    const year = now.getFullYear().toString().slice(-2)
    const month = (now.getMonth() + 1).toString().padStart(2, '0')
    
    return `I${year}${month}${reportCode.padStart(3, '0')}`
  }, [])

  // Load company locations
  const loadCompanyLocations = async (companyId: string) => {
    try {
      const { data, error } = await supabase
        .from('company_locations')
        .select('id, name, description, is_default')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('sort_order')

      if (error) throw error
      
      setCompanyLocations(data || [])
      
      // Auto-select default location if one exists
      const defaultLoc = data?.find(l => l.is_default)
      if (defaultLoc) {
        setSelectedLocation(defaultLoc.name)
      }
    } catch (err) {
      console.error('Failed to load locations:', err)
      setCompanyLocations([])
    }
  }

  // Handle company selection
  const handleSelectCompany = async (company: Company) => {
    setSelectedCompany(company)
    
    // Check if TechnipFMC is selected (case-insensitive match)
    const technipSelected = company.name.toLowerCase().includes('technipfmc') || 
                           company.name.toLowerCase().includes('technip fmc')
    setIsTechnipFMC(technipSelected)
    setServiceOrder('') // Reset service order when changing company
    
    // Generate order number
    const generatedOrder = generateOrderNumber(company.report_code)
    
    if (generatedOrder) {
      setOrderNumber(generatedOrder)
      setIsManualOrder(false)
    } else {
      setOrderNumber('')
      setIsManualOrder(true)
    }
    
    // Load locations for the company
    await loadCompanyLocations(company.id)
    
    // Move to next step
    setCurrentStep('order')
  }

  // Handle order number confirmation
  const handleConfirmOrder = () => {
    if (!orderNumber.trim()) {
      alert('Ordrenummer er påkrevd')
      return
    }
    
    // Check if company has locations
    if (companyLocations.length > 0) {
      setCurrentStep('location')
    } else {
      // Skip location step, go directly to samleliste
      navigateToSamleliste()
    }
  }

  // Handle location confirmation
  const handleConfirmLocation = () => {
    navigateToSamleliste()
  }

  // Navigate to the actual Samleliste page
  const navigateToSamleliste = () => {
    if (!selectedCompany) return
    
    // Store flow data in sessionStorage for SamlelistePage
    sessionStorage.setItem('samlelisteOrderNumber', orderNumber)
    sessionStorage.setItem('samlelisteLocation', selectedLocation)
    
    // Store service order if provided (TechnipFMC specific)
    if (serviceOrder.trim()) {
      sessionStorage.setItem('samlelisteServiceOrder', serviceOrder)
    } else {
      sessionStorage.removeItem('samlelisteServiceOrder')
    }
    
    navigate(`/samleliste/${selectedCompany.id}`)
  }

  // Go back one step
  const handleBack = () => {
    if (currentStep === 'location') {
      setCurrentStep('order')
    } else if (currentStep === 'order') {
      setCurrentStep('company')
      setSelectedCompany(null)
      setOrderNumber('')
    } else {
      navigate('/loft-selection')
    }
  }

  // Progress indicator
  const getStepNumber = () => {
    switch (currentStep) {
      case 'company': return 1
      case 'order': return 2
      case 'location': return 3
      default: return 1
    }
  }

  const totalSteps = companyLocations.length > 0 ? 3 : 2
  const stepNumber = getStepNumber()

  usePageHeader('Kontroll av løfteutstyr', {
    subtitle: `Steg ${stepNumber} av ${totalSteps}`,
    onBack: handleBack,
  })

  // Loading state
  if (loading && currentStep === 'company') {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <div className="text-center">
          <Loader2 className="w-16 h-16 animate-spin text-[#1E3A5F] mx-auto mb-4" />
          <span className="text-2xl text-stone-700">Laster...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'var(--bg-primary)' }}>
      <div className="shrink-0 px-4 py-3 bg-white border-b border-stone-200">
        <div className="flex gap-2">
          {Array.from({ length: totalSteps }, (_, i) => (
            <div
              key={i}
              className={`flex-1 h-1.5 rounded-full transition-colors ${
                i < stepNumber ? 'bg-[#1E3A5F]' : 'bg-stone-200'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* STEP 1: Company Selection */}
        {currentStep === 'company' && (
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <Building2 size={48} className="mx-auto text-[#1E3A5F] mb-3" />
              <h2 className="text-2xl font-semibold text-stone-800">Velg kunde</h2>
              <p className="text-stone-500 mt-1">Fritt søk</p>
            </div>

            {/* Search */}
            <div className="mb-4">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Trykk for å søke etter selskap..."
                className={`w-full px-5 py-4 text-xl border-2 border-stone-300 rounded-2xl focus:outline-none focus:border-[#1E3A5F] ${TOUCH.inputHeight} text-stone-800 placeholder:text-stone-400 bg-white`}
              />
            </div>

            {/* Quick-access company buttons */}
            {searchQuery.trim() === '' && commonCompanies.length > 0 && (
              <div className="mb-6">
                <p className="text-sm font-medium text-stone-500 mb-3">Mest brukt</p>
                <div className="flex flex-wrap gap-2">
                  {commonCompanies.map((company) => (
                    <button
                      key={company.id}
                      onClick={() => handleSelectCompany(company)}
                      className="px-4 py-2.5 bg-white border-2 border-stone-300 rounded-xl text-base font-medium text-stone-700 hover:border-[#1E3A5F] hover:text-[#1E3A5F] hover:bg-blue-50 active:scale-[0.97] transition-all"
                    >
                      {company.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Results */}
            {searchQuery.trim() === '' ? (
              <div className="text-center py-8">
                <p className="text-lg text-stone-400">
                  Eller søk etter annen kunde
                </p>
              </div>
            ) : filteredCompanies.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-lg text-stone-500">
                  Ingen selskap funnet for "{searchQuery}"
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredCompanies.map((company) => (
                  <button
                    key={company.id}
                    onClick={() => handleSelectCompany(company)}
                    className={`w-full flex items-center justify-between gap-4 p-5 bg-white border-2 border-stone-300 rounded-xl hover:border-[#1E3A5F] hover:shadow-md transition-all active:scale-[0.98] text-left group`}
                  >
                    <div className="flex-1">
                      <h3 className="text-xl font-medium text-stone-700 group-hover:text-[#1E3A5F]">
                        {company.name}
                      </h3>
                      {company.report_code && (
                        <p className="text-sm text-stone-400 mt-1">
                          Kode: {company.report_code}
                        </p>
                      )}
                    </div>
                    <ChevronRight className="text-stone-400 group-hover:text-[#1E3A5F]" size={24} />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* STEP 2: Order Number */}
        {currentStep === 'order' && selectedCompany && (
          <div className="max-w-xl mx-auto">
            <div className="text-center mb-8">
              <FileText size={48} className="mx-auto text-[#1E3A5F] mb-3" />
              <h2 className="text-2xl font-semibold text-stone-800">Ordrenummer</h2>
              <p className="text-stone-500 mt-1">{selectedCompany.name}</p>
            </div>

            {/* Order number display/input */}
            <div className="bg-white rounded-2xl border-2 border-stone-200 p-6 mb-6">
              {!isManualOrder ? (
                <>
                  {/* Auto-generated order number */}
                  <div className="text-center mb-4">
                    <p className="text-sm text-stone-500 mb-2">Automatisk generert</p>
                    <div className="text-4xl font-bold text-[#1E3A5F] tracking-wider font-mono">
                      {orderNumber}
                    </div>
                  </div>
                  
                  <button
                    onClick={() => setIsManualOrder(true)}
                    className={`w-full flex items-center justify-center gap-2 ${TOUCH.buttonHeight} px-4 py-3 text-stone-600 border-2 border-stone-300 rounded-xl hover:bg-stone-50 transition-colors`}
                  >
                    <Edit3 size={20} />
                    <span>Endre manuelt</span>
                  </button>
                </>
              ) : (
                <>
                  {/* Manual order number input */}
                  <label className="block text-sm font-semibold text-stone-600 mb-2">
                    Skriv inn ordrenummer
                  </label>
                  <input
                    type="text"
                    value={orderNumber}
                    onChange={(e) => setOrderNumber(e.target.value.toUpperCase())}
                    placeholder="F.eks. I2601004"
                    className={`w-full px-4 py-3 ${TOUCH.inputHeight} text-2xl font-mono text-center border-2 border-stone-300 rounded-xl focus:border-[#1E3A5F] focus:outline-none`}
                    autoFocus
                  />
                  
                  {selectedCompany.report_code && (
                    <button
                      onClick={() => {
                        setOrderNumber(generateOrderNumber(selectedCompany.report_code))
                        setIsManualOrder(false)
                      }}
                      className="w-full mt-4 text-sm text-[#1E3A5F] hover:underline"
                    >
                      Bruk auto-generert ordre
                    </button>
                  )}
                </>
              )}
            </div>

            {/* Service Order field - TechnipFMC only */}
            {isTechnipFMC && (
              <div className="bg-white rounded-2xl border-2 border-amber-300 p-6 mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                  <label className="text-sm font-semibold text-amber-700">
                    Service Order (valgfri)
                  </label>
                </div>
                <p className="text-xs text-stone-500 mb-3">
                  Hvis utfylt, vil Service Order brukes istedenfor Ordrenummer på kontrollkort og i database
                </p>
                <input
                  type="text"
                  value={serviceOrder}
                  onChange={(e) => setServiceOrder(e.target.value.toUpperCase())}
                  placeholder="Skriv inn Service Order..."
                  className={`w-full px-4 py-3 ${TOUCH.inputHeight} text-xl font-mono text-center border-2 border-amber-300 rounded-xl focus:border-amber-500 focus:outline-none bg-amber-50`}
                />
              </div>
            )}

            {/* Continue button */}
            <button
              onClick={handleConfirmOrder}
              disabled={!orderNumber.trim()}
              className={`w-full flex items-center justify-center gap-3 ${TOUCH.buttonHeight} px-6 py-4 bg-[#1E3A5F] text-white text-lg font-semibold rounded-2xl hover:bg-[#2a4a73] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <span>Neste</span>
              <ChevronRight size={24} />
            </button>
          </div>
        )}

        {/* STEP 3: Location Selection */}
        {currentStep === 'location' && selectedCompany && (
          <div className="max-w-xl mx-auto">
            <div className="text-center mb-8">
              <MapPin size={48} className="mx-auto text-[#1E3A5F] mb-3" />
              <h2 className="text-2xl font-semibold text-stone-800">Lokasjon</h2>
              <p className="text-stone-500 mt-1">{selectedCompany.name}</p>
            </div>

            {/* Location options */}
            <div className="space-y-3 mb-6">
              {companyLocations.map((location) => (
                <button
                  key={location.id}
                  onClick={() => {
                    // Toggle selection - deselect if already selected
                    if (selectedLocation === location.name) {
                      setSelectedLocation('')
                    } else {
                      setSelectedLocation(location.name)
                    }
                  }}
                  className={`w-full flex items-center justify-between gap-4 p-5 bg-white border-2 rounded-xl transition-all active:scale-[0.98] text-left ${
                    selectedLocation === location.name
                      ? 'border-[#1E3A5F] bg-blue-50'
                      : 'border-stone-300 hover:border-stone-400'
                  }`}
                >
                  <div className="flex-1">
                    <h3 className={`text-xl font-medium ${
                      selectedLocation === location.name ? 'text-[#1E3A5F]' : 'text-stone-700'
                    }`}>
                      {location.name}
                    </h3>
                    {location.description && (
                      <p className="text-sm text-stone-400 mt-1">
                        {location.description}
                      </p>
                    )}
                    {location.is_default && (
                      <span className="inline-block mt-2 px-2 py-0.5 text-xs font-medium text-[#1E3A5F] bg-blue-100 rounded">
                        Standard
                      </span>
                    )}
                  </div>
                  {selectedLocation === location.name && (
                    <Check className="text-[#1E3A5F]" size={24} />
                  )}
                </button>
              ))}
              
              {/* Manual location option */}
              <div className="pt-4 border-t border-stone-200">
                <label className="block text-sm font-semibold text-stone-600 mb-2">
                  Eller skriv inn manuelt
                </label>
                <input
                  type="text"
                  value={!companyLocations.some(l => l.name === selectedLocation) ? selectedLocation : ''}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  placeholder="Skriv inn lokasjon her"
                  className={`w-full px-4 py-3 ${TOUCH.inputHeight} text-base border-2 border-stone-300 rounded-xl focus:border-[#1E3A5F] focus:outline-none`}
                />
              </div>
            </div>

            {/* Continue button */}
            <button
              onClick={handleConfirmLocation}
              className={`w-full flex items-center justify-center gap-3 ${TOUCH.buttonHeight} px-6 py-4 bg-[#1E3A5F] text-white text-lg font-semibold rounded-2xl hover:bg-[#2a4a73] active:scale-[0.98] transition-all`}
            >
              <span>Start kontroll</span>
              <ChevronRight size={24} />
            </button>
            
            {/* Skip location option */}
            <button
              onClick={() => {
                setSelectedLocation('')
                navigateToSamleliste()
              }}
              className="w-full mt-3 text-stone-500 hover:text-stone-700 text-sm"
            >
              Hopp over lokasjon
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default SamlelisteFlow

